import { Children, Fragment, ReactNode } from 'react';

import {
  Box,
  Divider,
  MetricsBox,
  SquareMarker,
  Stack,
  tokens,
} from '@cast/design-system';

// ─── Value formatter ─────────────────────────────────────────────────────────

/**
 * Default summary value formatter: rounds to the nearest integer and
 * formats with locale-aware thousand separators.
 */
const formatSummaryValue = (value: number): string =>
  Math.round(value).toLocaleString('en-US');

// ─── SummaryMetric ───────────────────────────────────────────────────────────

type SummaryMetricProps = {
  label: string;
  value: number;
  unit: string;
  color?: string;
  isSelected?: boolean;
  onClick?: () => void;
  /**
   * Custom value formatter. Defaults to `formatSummaryValue` (integer
   * rounding with locale separators). Pass a 2-decimal formatter for DBO.
   */
  formatValue?: (value: number) => string;
};

export const SummaryMetric = ({
  label,
  value,
  unit,
  color,
  isSelected = true,
  onClick,
  formatValue = formatSummaryValue,
}: SummaryMetricProps) => (
  <Box
    onClick={onClick}
    onKeyDown={
      onClick
        ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    aria-pressed={onClick ? isSelected : undefined}
    sx={{
      flex: '1 1 0',
      minWidth: 0,
      cursor: onClick ? 'pointer' : undefined,
      opacity: isSelected ? 1 : 0.4,
      transition: 'opacity 150ms',
    }}
  >
    <MetricsBox
      size="m"
      slots={{
        startContent: color ? <SquareMarker color={color} /> : undefined,
        label,
        value: formatValue(value),
        unit: unit || undefined,
      }}
      slotProps={{
        label: {
          color: tokens.theme.fg.structural.secondary,
          sx: { textTransform: 'uppercase' },
        },
        value: {
          variant: 'P18B',
          color: tokens.theme.fg.structural.primary,
        },
        unit: {
          sx: { textTransform: 'uppercase' },
        },
      }}
    />
  </Box>
);

// ─── SummaryMetricsRow ───────────────────────────────────────────────────────

export const SummaryMetricsRow = ({ children }: { children: ReactNode }) => {
  const items = Children.toArray(children);

  return (
    <Stack
      direction={{ none: 'column', laptop: 'row' }}
      alignItems="stretch"
      gap={12}
      sx={{ '& > *:not(.MuiDivider-root)': { flex: 1, minWidth: 0 } }}
    >
      {items.map((child, index) => (
        <Fragment key={index}>
          {index > 0 ? (
            <Divider
              flexItem
              orientation="vertical"
              sx={{ display: { none: 'none', laptop: 'block' } }}
            />
          ) : null}
          {child}
        </Fragment>
      ))}
    </Stack>
  );
};

// ─── SummaryMetricsSkeleton ──────────────────────────────────────────────────

export const SummaryMetricsSkeleton = () => (
  <Stack direction="row" gap={12}>
    {Array.from({ length: 4 }, (_, i) => (
      <Box key={i} flex={1} minWidth={0}>
        <MetricsBox
          size="m"
          slots={{
            label: '—',
            value: '—',
          }}
          slotProps={{
            label: { color: tokens.theme.fg.structural.secondary },
          }}
        />
      </Box>
    ))}
  </Stack>
);
